import { OrderAside } from '@/components/orders/OrderAside'
import { ORDER_ASIDE_SUBTLE_SURFACE_CLASS } from '@/components/orders/order-aside.styles'
import { IndexDataTable } from '@/components/tables/IndexTable'
import { getTransactionDisplay } from '@/components/transactions/getTransactionDisplay'
import { Badge } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'
import { useAbility } from '@/lib/casl'
import { TransactionLineType } from '@/lib/enums/TransactionLineType'
import type {
  SalesSummaryReportCollectionLine,
  SalesSummaryReportCollectionRow,
} from '@/lib/services/reports/reports.types'
import { cn, formatCurrency, parseCurrency } from '@/lib/utils'
import { Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { ReceiptText } from 'lucide-react'
import { usePathHelper } from '@/hooks/usePathHelper'
import { formatTransactionDate } from './-formatters'
import { MISSING_CUSTOMER_LABEL, getCustomerDisplayName, hasProvidedCustomer } from './-helpers'

type TransactionLine = SalesSummaryReportCollectionLine

/** A line with what it comes to. */
type TransactionLineRow = TransactionLine & { lineTotal: number }

const transactionLineColumns: ColumnDef<TransactionLineRow>[] = [
  {
    accessorKey: 'memo',
    header: 'Memo',
    enableSorting: false,
    cell: ({ row }) => <TransactionLineMemo line={row.original} />,
    meta: { kind: 'primary', label: 'Memo' },
  },
  {
    accessorKey: 'type',
    header: 'Type',
    enableSorting: false,
    cell: ({ row }) => <TransactionLineTypeBadge type={row.original.type} />,
    meta: { kind: 'badge', label: 'Type' },
  },
  {
    accessorKey: 'lineTotal',
    header: 'Amount',
    enableSorting: false,
    cell: ({ row }) => (
      <span className="font-medium">{formatCurrency(row.original.lineTotal)}</span>
    ),
    meta: { kind: 'money', label: 'Amount', align: 'end' },
  },
]

type SalesSummaryTransactionAsideProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  transaction: SalesSummaryReportCollectionRow | null
  /** The row's florist, for its order and customer links. */
  orgId: number
  timezone: string
}

export function SalesSummaryTransactionAside({
  open,
  onOpenChange,
  transaction,
  orgId,
  timezone,
}: SalesSummaryTransactionAsideProps) {
  return (
    <OrderAside open={open} onOpenChange={onOpenChange}>
      {transaction ? (
        <SalesSummaryTransactionAsideContent
          transaction={transaction}
          orgId={orgId}
          timezone={timezone}
        />
      ) : null}
    </OrderAside>
  )
}

function SalesSummaryTransactionAsideContent({
  transaction,
  orgId,
  timezone,
}: Omit<SalesSummaryTransactionAsideProps, 'open' | 'onOpenChange' | 'transaction'> & {
  transaction: SalesSummaryReportCollectionRow
}) {
  const ability = useAbility()
  const path = usePathHelper()
  const canViewTransactionLines = ability.can('view', 'transactions')
  const display = getTransactionDisplay(transaction)
  const hasCustomer = hasProvidedCustomer(transaction)
  const customerName = getCustomerDisplayName(transaction)
  const salesOrderLink = transaction.sales_order_id
    ? path.salesOrders.show(transaction.sales_order_id).using({ orgId }).link
    : null
  const customerLink = transaction.purchaser_customer_id
    ? path.customers.show(transaction.purchaser_customer_id).using({ orgId }).link
    : null

  return (
    <div className="space-y-5 px-5 py-5 sm:px-6 sm:py-6">
      <div className="min-w-0 pr-12">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <Typography variant="h3" className="pb-0">
              Transaction Details
            </Typography>
            <Badge variant={display.badgeVariant}>{display.label}</Badge>
          </div>
          <Typography variant="supporting" className="mt-2 block">
            {formatTransactionDate(transaction.created_at, timezone)}
          </Typography>
        </div>
      </div>

      <div className="border-t border-border/60" />

      <section className="space-y-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          {transaction.sales_order_id ? (
            salesOrderLink ? (
              <Link {...salesOrderLink} className="font-medium text-foreground hover:underline">
                Order #{transaction.sales_order_id}
              </Link>
            ) : (
              <span className="font-medium text-foreground">
                Order #{transaction.sales_order_id}
              </span>
            )
          ) : null}
          {customerLink ? (
            <Link {...customerLink} className="font-medium text-foreground hover:underline">
              {customerName}
            </Link>
          ) : hasCustomer ? (
            <span className="font-medium text-foreground">{customerName}</span>
          ) : (
            <Typography variant="supporting" italic className="block">
              {MISSING_CUSTOMER_LABEL}
            </Typography>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ReceiptText className="size-4 text-muted-foreground" />
          <Typography variant="label" tone="muted" className="block">
            Detailed breakdown of items included in this transaction.
          </Typography>
        </div>

        {parseCurrency(transaction.deposit_amount) !== 0 ? (
          <TransactionTotalRow
            label="Deposits"
            value={formatCurrency(transaction.deposit_amount)}
          />
        ) : null}

        {canViewTransactionLines ? (
          <TransactionLinesSection transaction={transaction} />
        ) : (
          <div className={cn('space-y-2 px-4 py-5', ORDER_ASIDE_SUBTLE_SURFACE_CLASS)}>
            <Typography variant="label" className="block">
              Line items unavailable
            </Typography>
            <Typography variant="supporting" className="block">
              Line item details aren't available with your current access.
            </Typography>
          </div>
        )}
      </section>
    </div>
  )
}

// What the row's receipt itemized.
function TransactionLinesSection({
  transaction,
}: {
  transaction: SalesSummaryReportCollectionRow
}) {
  const lines = transaction.lines
  const isEstimated = lines.some((line) => line.is_estimated)
  const transactionLineRows = lines.map(
    (line): TransactionLineRow => ({
      ...line,
      lineTotal: parseCurrency(line.amount) * (line.quantity || 1),
    })
  )

  if (!lines.length) {
    return (
      <div className={cn('space-y-2 px-4 py-5', ORDER_ASIDE_SUBTLE_SURFACE_CLASS)}>
        <Typography variant="label" className="block">
          No line items
        </Typography>
        <Typography variant="supporting" className="block">
          No line items were found for this transaction.
        </Typography>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <IndexDataTable
        mode="client"
        data={transactionLineRows}
        columns={transactionLineColumns}
        pagination={false}
      />
      <div className={cn('divide-y divide-border/60', ORDER_ASIDE_SUBTLE_SURFACE_CLASS)}>
        <TransactionTotalRow
          label={`Payout${isEstimated ? ' (estimated)' : ''}`}
          value={formatCurrency(transaction.net_sales)}
        />
        <TransactionTotalRow
          label={`Total${isEstimated ? ' (estimated)' : ''}`}
          value={formatCurrency(transaction.cash_total)}
        />
      </div>
    </div>
  )
}

function TransactionLineMemo({ line }: { line: TransactionLine }) {
  return (
    <div className="text-sm">
      {line.memo || <MemoFallback line={line} />}
      {line.is_estimated ? (
        <span className="ml-1 text-xs text-muted-foreground">(estimated)</span>
      ) : null}
    </div>
  )
}

function TransactionLineTypeBadge({ type }: { type: TransactionLine['type'] }) {
  return (
    <Badge variant="outline" size="xs" className="capitalize">
      {formatLineType(type)}
    </Badge>
  )
}

function TransactionTotalRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3 text-sm font-semibold">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums text-foreground">{value}</span>
    </div>
  )
}

function MemoFallback({ line }: { line: TransactionLine }) {
  const fallbackText = (() => {
    switch (line.type) {
      case TransactionLineType.Product.Value:
        return 'Product sale'
      case TransactionLineType.Tax.Value:
        return 'Sales tax'
      case TransactionLineType.MerchantFee.Value:
        return 'Payment processing fee'
      case TransactionLineType.PlatformFee.Value:
        return 'Platform service fee'
      case TransactionLineType.PosFee.Value:
        return 'POS processing fee'
      case TransactionLineType.Delivery.Value:
        return 'Delivery fee'
      case TransactionLineType.RelayFee.Value:
        return 'Relay fee'
      case TransactionLineType.Shipping.Value:
        return 'Shipping fee'
      case TransactionLineType.Discount.Value:
        return 'Applied discount'
      case TransactionLineType.Tip.Value:
      case TransactionLineType.VariableServiceFee.Value:
      case TransactionLineType.FloristFee.Value:
      default:
        return 'Transaction line item'
    }
  })()

  return <span className="text-muted-foreground">{fallbackText}</span>
}

function formatLineType(type: TransactionLine['type']) {
  return type.replaceAll('_', ' ')
}
